import { StyleFilterPublicIcon } from '@/components/Icons/StyleFilterPublicIcon';
import StyleFilterDetailCard from '@/components/StyleFilterDetailCard/StyleFilterDetailCard';
import { TrackList } from '@/components/TrackList';
import { Badge } from '@/components/ui/badge';
import { useDimensions } from '@/hooks/useDimensions';
import { getStyleFilterArtistSFGeneration, getStyleFilterGroupDetail } from '@/service/filter';
import { useRequest } from 'ahooks';
import { isEmpty } from 'lodash-es';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFilterAnalyticsData } from '../hooks/useFilterAnalyticsData';
import StyleFilterDetailMobile from './StyleFilterDetailMobile';

interface StyleFilterDetailProps {
  title?: string;
  imageUrl?: string;
  isPublic?: boolean;
  description?: string;
  stats?: {
    totalPurchases?: number;
    tracksGenerated?: number;
    secondsGenerated?: number;
    revenueGenerated?: number;
  };
}

const StyleFilterDetail: React.FC<StyleFilterDetailProps> = () => {
  const navigate = useNavigate();

  const { isMobileOrPad } = useDimensions();

  const [searchParams] = useSearchParams();
  const path = window.location.pathname;
  const id = path.split('/').pop();
  const isPublic = searchParams.get('isPublic') === 'true';

  const { data: getStyleFilterGroupDetailData } = useRequest(() => getStyleFilterGroupDetail(Number(id)), {
    ready: !!id,
  });

  const [strength, setStrength] = useState<'low' | 'high'>('low');

  const [styleFilterId, setStyleFilterId] = useState<number>(getStyleFilterGroupDetailData?.data?.sf_list[0]?.id ?? 0);

  const { processedStatsData } = useFilterAnalyticsData(isPublic, id, strength);

  const handleSwitch = (data: { value: number; id: number | undefined }) => {
    setStrength(data.value === 0 ? 'low' : 'high');
    setStyleFilterId(data.id ?? 0);
    runGetStyleFilterArtistSFGeneration(1, 10, Number(id), data.id ?? 0);
  };

  useEffect(() => {
    setStyleFilterId(getStyleFilterGroupDetailData?.data?.sf_list[0]?.id ?? 0);
  }, [getStyleFilterGroupDetailData]);

  const { data: getStyleFilterArtistSFGenerationData, run: runGetStyleFilterArtistSFGeneration } = useRequest(
    (page: number, pageSize: number, styleFilterGroupId: number, styleFilterId: number) =>
      getStyleFilterArtistSFGeneration({ page, pageSize, styleFilterGroupId: styleFilterGroupId, stylefilterId: styleFilterId }),
    {
      ready: !!id && !!styleFilterId,
      defaultParams: [1, 10, Number(id), styleFilterId],
    },
  );

  if (isMobileOrPad) {
    return (
      <StyleFilterDetailMobile
        id={id}
        isPublic={isPublic}
        processedStatsData={processedStatsData}
        getStyleFilterArtistSFGenerationData={getStyleFilterArtistSFGenerationData}
        runGetStyleFilterArtistSFGeneration={runGetStyleFilterArtistSFGeneration}
        getStyleFilterGroupDetailData={getStyleFilterGroupDetailData}
        styleFilterId={styleFilterId}
        handleSwitch={handleSwitch}
      />
    );
  }

  return (
    <div className="flex flex-col bg-[#111112] text-white">
      <div className="p-6 border-b border-[#242425]">
        <div
          className="hover:text-white flex items-center text-sm w-fit cursor-pointer"
          onClick={() => {
            navigate('/style-filter-admin');
          }}
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          All StyleFilters™
        </div>
      </div>

      <div className="p-12 lg:mx-24 min-h-screen flex flex-col gap-16 mx-auto bg-[#111112]">
        <div className="flex flex-row">
          <div>
            {getStyleFilterGroupDetailData?.data?.id && (
              <StyleFilterDetailCard
                onRemove={() => undefined}
                onUse={() => undefined}
                title={getStyleFilterGroupDetailData?.data?.name}
                content={''}
                onSwitch={handleSwitch}
                sfList={getStyleFilterGroupDetailData?.data?.sf_list}
                sfId={getStyleFilterGroupDetailData?.data?.id}
                background={getStyleFilterGroupDetailData?.data?.album_cover}
                showButtons={false}
                disabled={true}
                fontSize={9}
                isMarket={true}
                coverStyle={{
                  backgroundSize: 'cover',
                  backgroundRepeat: 'no-repeat',
                  height: 348,
                  width: 230,
                  marginBottom: '25px',
                  boxShadow: `
                      0.6px 0.6px 0.6px rgba(0, 0, 0, 0.019),
                      1.4px 1.3px 1.5px rgba(0, 0, 0, 0.028),
                      2.6px 2.5px 2.9px rgba(0, 0, 0, 0.035),
                      4.7px 4.5px 5.2px rgba(0, 0, 0, 0.041),
                      8.8px 8.4px 9.3px rgba(0, 0, 0, 0.05),
                      21px 20px 21px rgba(0, 0, 0, 0.07)
                    `,
                }}
              />
            )}
          </div>
          <div className="ml-16">
            <div className="flex flex-col items-start gap-[21px]">
              {/* Public status badge */}
              <Badge className="flex bg-[#4CAF00]/20 text-[#4CAF00] items-center gap-1.5 px-4 py-3 border-contextual-colors-positive-accent rounded-[100px] border-none">
                <span>
                  <StyleFilterPublicIcon />
                </span>
                <span className="font-mono text-xs tracking-[-0.24px] leading-6">PUBLIC</span>
              </Badge>

              {/* Project title */}
              <h1 className="mt-2self-stretch font-normal text-contextual-colors-content-primary text-[64px] tracking-[-2.56px] leading-[64px]">
                {getStyleFilterGroupDetailData?.data?.name}
              </h1>
            </div>
          </div>
        </div>
        <div className="flex flex-row">
          <div className="w-full inline-flex flex-col justify-start items-start gap-6">
            {isPublic && (
              <>
                <div className="self-stretch inline-flex justify-between items-start">
                  <div className="justify-start text-[#71717A] text-Content-Tertiary text-xl font-normal leading-tight">
                    Total Purchases
                  </div>
                  <div className="justify-start text-xl font-normal leading-tight">{processedStatsData?.[0]?.value}</div>
                </div>
                <div className="self-stretch inline-flex justify-between items-start">
                  <div className="justify-start text-[#71717A] text-Content-Tertiary text-xl font-normal  leading-tight">
                    Tracks Generated
                  </div>
                  <div className="justify-start text-xl font-normal leading-tight">{processedStatsData?.[1]?.value}</div>
                </div>
                <div className="self-stretch inline-flex justify-between items-start">
                  <div className="justify-start text-[#71717A] text-Content-Tertiary text-xl font-normal leading-tight">
                    Seconds Generated
                  </div>
                  <div className="justify-start text-xl font-normal leading-tight">{processedStatsData?.[2]?.value}</div>
                </div>
                <div className="self-stretch inline-flex justify-between items-start">
                  <div className="justify-start text-[#71717A] text-Content-Tertiary text-xl font-normal leading-tight">
                    Income Generated
                  </div>
                  <div className="justify-start text-xl font-normal leading-tight">{processedStatsData?.[3]?.value}</div>
                </div>
              </>
            )}
          </div>
        </div>
        {!isEmpty(getStyleFilterArtistSFGenerationData?.data) && (
          <div className="flex flex-row">
            <section className="bg-[#111112] rounded-2xl w-full">
              <h2 className="text-[32px] text-[#48484b] tracking-[-0.96px] leading-10 mb-6">Tracks using this StyleFilter</h2>
              <TrackList
                ifNavigateInFilterTag={false}
                trackListData={getStyleFilterArtistSFGenerationData?.data}
                showSorter={false}
                theme="dark"
                count={getStyleFilterArtistSFGenerationData?.count}
                onPageChange={(page) => {
                  runGetStyleFilterArtistSFGeneration(page, 10, Number(id), styleFilterId ?? 0);
                }}
                isSliceData={false}
              />
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default StyleFilterDetail;
